/** Centre-screen pop-out listing one tactical position's complete ranked group. */
import { SearchIcon, XIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Input } from "@/components/atoms/Input";
import {
  Modal,
  ModalBackdrop,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/atoms/Modal";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PitchSaveStatusLine, {
  type PitchSaveStatusLineProps,
} from "@/features/scouting/components/molecules/PitchSaveStatusLine";
import type { WatchlistMoveDestination } from "@/features/scouting/components/molecules/WatchlistOrderControls/WatchlistOrderControlsContainer";
import WatchlistOrderDndContext from "@/features/scouting/components/molecules/WatchlistOrderDndContext";
import WatchlistPositionPopoutRow, {
  type PositionPopoutRow,
  type PositionPopoutRowAction,
} from "@/features/scouting/components/molecules/WatchlistPositionPopoutRow";
import type { TacticalSlot } from "@/generated/graphql";

export type { PositionPopoutRow };

/**
 * SE-8427 (PV24, frame 947:16241): what opened the pop-out decides its copy.
 * `targets` is a "View N targets" look at a pool; `group` is a card's Show
 * all over the slot's ranked group.
 */
export type PositionPopoutOpening = "targets" | "group";

/** The footer legend when the pop-out lists the ● squad (SE-8535). */
export const SQUAD_LEGEND = "● already in your squad";

/**
 * The title the frame draws for each opening. A group titles its ranked
 * targets; a slot that holds only ● squad players titles those (SE-8535).
 * @param slot - Tactical slot
 * @param opening - What opened the pop-out
 * @param count - Ranked targets before the search filter
 * @param squadCount - ● squad players listed above them
 * @returns The title text
 */
const openingTitle = (
  slot: TacticalSlot,
  opening: PositionPopoutOpening,
  count: number,
  squadCount: number
): string => {
  if (opening === "targets") return `${slot} · ${count} targets`;
  if (count === 0 && squadCount > 0) {
    return `${slot} · ${squadCount} in your squad`;
  }
  return `${slot} · ${count} ${count === 1 ? "player" : "players"}, ranked 1–${count}`;
};

/** No rows: a stable default so the memoised View does not re-render. */
const NO_ROWS: readonly PositionPopoutRow[] = [];

/**
 * The opener's copy, with the ● legend added when squad rows are listed and
 * no legend of its own is set (SE-8535).
 * @param copy - The opener's copy, if any
 * @param squadCount - ● squad rows listed
 * @returns The copy the footer reads
 */
const squadCopy = (
  copy: PositionPopoutCopy | undefined,
  squadCount: number
): PositionPopoutCopy | undefined =>
  squadCount > 0 && !copy?.legend ? { ...copy, legend: SQUAD_LEGEND } : copy;

/** Longest search echoed back before it is cut (SE-8526). */
const NO_MATCH_ECHO_MAX = 24;

/**
 * "No players match “xyz”" (Figma 944:16283): echoing the search lets the
 * reader spot a typo without going back to the field; a long query is cut so
 * the line never breaks the pop-out.
 * @param search - The search text
 * @returns The empty-state line.
 */
export const noMatchCopy = (search: string): string => {
  const term = search.trim();
  const echoed =
    term.length > NO_MATCH_ECHO_MAX
      ? `${term.slice(0, NO_MATCH_ECHO_MAX)}…`
      : term;
  return `No players match “${echoed}”`;
};

/**
 * SE-8393: the copy an opener may replace — the scenario slot pop-out reads
 * its title, placeholder, legend and empty-search line off Figma 947:18091;
 * the watchlist keeps the defaults.
 */
export interface PositionPopoutCopy {
  /** Replaces the opening's title and hides the pool caption. */
  readonly title?: string;
  readonly searchPlaceholder?: string;
  /** Footer legend, e.g. "Drag to reorder · ● already in your squad". */
  readonly legend?: string;
}

/** Prepared props for the pop-out presentation. */
export interface WatchlistPositionPopoutViewProps {
  readonly slot: TacticalSlot;
  readonly isOpen: boolean;
  /** SE-8393: the opener's copy; absent = the watchlist's. */
  readonly copy?: PositionPopoutCopy;
  /** SE-8393: root test id; default `watchlist-position-popout`. */
  readonly testID?: string;
  /**
   * SE-8393: the D5 save-status line under the title — Saving… · Saved just
   * now · Updated by {editor} · Reload — when the opener owns a save state.
   */
  readonly saveStatus?: PitchSaveStatusLineProps;
  /** SE-8427: what opened the pop-out; default `group` (a card's Show all). */
  readonly opening?: PositionPopoutOpening;
  readonly rows: readonly PositionPopoutRow[];
  /**
   * SE-8535: the ● squad players at the slot, listed read-only and unranked
   * above the targets (the watchlist's card opening). Default: none.
   */
  readonly squadRows?: readonly PositionPopoutRow[];
  /**
   * SE-8535: every ● squad player at the slot, before the search filter —
   * the squad-only title counts them all. Default: `squadRows.length`.
   */
  readonly squadTotal?: number;
  readonly totalCount: number;
  /** The Table slot scope is list-only; the all-watchlists pool has no hand-off. */
  readonly showOpenInTable: boolean;
  readonly search: string;
  readonly isLoading: boolean;
  /** Later-page load failure, with a retry for the failed cursor. */
  readonly pageError: string | null;
  readonly canEdit: boolean;
  readonly playerNames: Readonly<Record<string, string>>;
  readonly onClose: () => void;
  readonly onSearchChange: (value: string) => void;
  readonly onClearSearch: () => void;
  readonly onMove: (
    playerId: string,
    destination: WatchlistMoveDestination
  ) => void;
  readonly onReorder: (orderedPlayerIds: readonly string[]) => void;
  readonly onOpenInTable: () => void;
  readonly onRetryPage: () => void;
  /**
   * PV22 (SE-8372): the blue + per row that places the target in the
   * scenario at this slot, with the footer caption "Pick a target to place
   * at {SLOT}". Absent on the watchlist pop-out and for a viewer.
   */
  readonly rowAction?: PositionPopoutRowAction;
}

/**
 * The footer: PV22's pick caption, SE-8393's legend or SE-8427's targets
 * count on the left, the Table hand-off on the right; nothing when none
 * applies.
 * @param props - The view props the footer reads
 * @param props.slot - Tactical slot, for the pick caption
 * @param props.copy - The opener's copy, for the legend
 * @param props.opening - What opened the pop-out, for the targets count
 * @param props.totalCount - Group size before the search filter
 * @param props.showOpenInTable - Whether the Table hand-off renders
 * @param props.rowAction - PV22's + per row, whose caption the footer carries
 * @param props.onOpenInTable - Opens the Table filtered to this position
 * @returns The footer, or null
 */
const renderFooter = ({
  slot,
  copy,
  opening,
  totalCount,
  showOpenInTable,
  rowAction,
  onOpenInTable,
}: Pick<
  WatchlistPositionPopoutViewProps,
  | "slot"
  | "copy"
  | "opening"
  | "totalCount"
  | "showOpenInTable"
  | "rowAction"
  | "onOpenInTable"
>) => {
  // The frame's "{N} targets in this position" (947:16241) — only when no
  // other caption owns the left side: PV22's pick caption and an opener's
  // legend keep their footers as their own frames draw them.
  const showTargetsCount = opening === "targets" && !rowAction && !copy?.legend;
  const hasCaption = Boolean(rowAction || copy?.legend || showTargetsCount);
  if (!showOpenInTable && !hasCaption) return null;
  return (
    // SE-8486 (W74): a 48 px foot on the raised surface, inset 20 — the
    // frame's only tinted section.
    <ModalFooter
      testID="watchlist-position-popout-foot"
      className="min-h-12 bg-surface-raised px-5"
    >
      <Stack
        testID="watchlist-position-popout-footer"
        direction="horizontal"
        // The hand-off sits on the right (383:5278): beside a caption it
        // shares the row, alone it must not fall to the left (SE-8442).
        className={`w-full items-center gap-2 ${
          hasCaption ? "justify-between" : "justify-end"
        }`}
      >
        {rowAction ? (
          <Text
            testID="watchlist-position-popout-pick-caption"
            variant="caption"
            className="text-content-muted"
          >
            Pick a target to place at {slot}
          </Text>
        ) : null}
        {copy?.legend ? (
          <Text
            testID="watchlist-position-popout-legend"
            variant="caption"
            className="text-content-muted"
          >
            {copy.legend}
          </Text>
        ) : null}
        {showTargetsCount ? (
          <Text
            testID="watchlist-position-popout-count-caption"
            variant="caption"
            className="text-content-muted"
          >
            {totalCount} targets in this position
          </Text>
        ) : null}
        {showOpenInTable ? (
          <Button
            testID="watchlist-position-popout-open-table"
            label="Open in table view →"
            // An accent text link, as the frame draws it — not the neutral
            // slab the audit of 2026-09-19 measured (SE-8442). The label
            // variant is what puts Satoshi on it: the vendored ButtonText
            // carries no font family (SE-8486, W74 — lot B's W30 fix).
            variant="link"
            labelTone="accent"
            labelVariant="caption-strong"
            size="sm"
            className="h-auto p-0"
            onPress={onOpenInTable}
          />
        ) : null}
      </Stack>
    </ModalFooter>
  );
};

/**
 * Renders the ranked group with search, reorder controls and the table hand-off.
 * @param props - Prepared rows, search state and callbacks.
 * @param props.slot - Tactical slot the group belongs to.
 * @param props.isOpen - Whether the pop-out is open.
 * @param props.copy - SE-8393: the opener's copy, when it replaces the watchlist's.
 * @param props.testID - SE-8393: the root test id.
 * @param props.saveStatus - SE-8393: the save-status line, when the opener owns one.
 * @param props.opening - SE-8427: what opened the pop-out; decides the copy.
 * @param props.rows - Complete ranked group after the search filter.
 * @param props.squadRows - SE-8535: the slot's ● squad, read-only, unranked.
 * @param props.squadTotal - SE-8535: the whole squad, before the search filter.
 * @param props.totalCount - Group size before the search filter.
 * @param props.showOpenInTable - Whether the Table hand-off renders.
 * @param props.search - Current search text.
 * @param props.isLoading - Whether pages are still loading.
 * @param props.pageError - Later-page load failure, when one happened.
 * @param props.canEdit - Whether reorder controls are enabled.
 * @param props.playerNames - Accessible names keyed by player identity.
 * @param props.onClose - Closes the pop-out.
 * @param props.onSearchChange - Updates the search text.
 * @param props.onClearSearch - Clears the search text.
 * @param props.onMove - Moves one player relative to the group.
 * @param props.onReorder - Applies a dragged order for the group.
 * @param props.onOpenInTable - Opens the Table filtered to this position.
 * @param props.onRetryPage - Retries the failed page.
 * @param props.rowAction - PV22: the + per row, when the opening offers one.
 */
const WatchlistPositionPopoutView = ({
  slot,
  isOpen,
  copy,
  testID = "watchlist-position-popout",
  saveStatus,
  opening = "group",
  rows,
  squadRows = NO_ROWS,
  squadTotal,
  totalCount,
  showOpenInTable,
  search,
  isLoading,
  pageError,
  canEdit,
  playerNames,
  onClose,
  onSearchChange,
  onClearSearch,
  onMove,
  onReorder,
  onOpenInTable,
  onRetryPage,
  rowAction,
}: WatchlistPositionPopoutViewProps) => (
  // SE-8486 (W70/S32): the frame anchors a 560 box 120 px from the top with
  // the lg radius; its sections are full-bleed and own their own 20 px inset,
  // so the box carries no padding of its own. `full` hands the width to the
  // component-named cap — the vendored `md` capped it at 510. The root's
  // `px-4` insets the box on screens narrower than 592 (invisible above);
  // `max-h-full` on the box + `shrink` on the body keep the raised foot on
  // screen on a 720 px viewport (the overlay root is a fixed absoluteFill).
  <Modal
    testID="watchlist-position-popout-modal"
    isOpen={isOpen}
    onClose={onClose}
    size="full"
    className="justify-start px-4 pt-pitch-popout-top"
  >
    <ModalBackdrop />
    <ModalContent
      testID={testID}
      className="max-h-full max-w-pitch-popout rounded-lg p-0"
    >
      {/* W71: the head is inset 20 on a 48 px row (the frame's 52 has no
          closed rung — ties round down) with the title at 16/24. SE-8486
          (W71/S35, Lot D8): title only — the pool subtitle ("Ranked by saved
          preference" / "All watchlists · by natural position") is gone. */}
      <ModalHeader
        testID="watchlist-position-popout-head"
        className="px-5 py-3"
      >
        <Stack className="gap-micro">
          <Text variant="title-sm" className="text-content-primary">
            {copy?.title ??
              openingTitle(
                slot,
                opening,
                totalCount,
                squadTotal ?? squadRows.length
              )}
          </Text>
          {saveStatus ? <PitchSaveStatusLine {...saveStatus} /> : null}
        </Stack>
        <ModalCloseButton
          testID="watchlist-position-popout-close"
          accessibilityLabel="Close position pop-out"
          onPress={onClose}
        >
          {/* The atom draws nothing of its own: self-closed, this was a 0×0
              button nobody could see or click (SE-8337's nightly). */}
          <Icon as={XIcon} size="sm" className="text-content-muted" />
        </ModalCloseButton>
      </ModalHeader>
      {/* The body is full-bleed (W70): each section below owns its own 20 px
          inset. A Box, not the vendored ModalBody — that one is a ScrollView
          with its own `mt-2 mb-6`, which the frame does not draw and a margin
          reset may not undo (no-margin rule); the rows list scrolls itself.
          `shrink` lets the body give way so the foot stays on screen. */}
      <Box testID="watchlist-position-popout-body" className="min-h-0 shrink">
        <Stack>
          {/* W72: the 36 px search on a 48 px wrap inset 20 (520 wide in a
              560 box). */}
          <Box
            testID="watchlist-position-popout-search-wrap"
            className="h-12 justify-center px-5"
          >
            <Input
              variant="outline"
              size="sm"
              className="h-9 rounded-sm"
              leadingIcon={SearchIcon}
              trailingIcon={search ? XIcon : undefined}
              onTrailingIconPress={search ? onClearSearch : undefined}
              field={{
                testID: "watchlist-position-popout-search",
                placeholder:
                  copy?.searchPlaceholder ?? "Search in this position…",
                value: search,
                onChangeText: onSearchChange,
              }}
            />
          </Box>
          {isLoading ? (
            <Text variant="caption" className="px-5 py-2 text-content-muted">
              Loading the complete group…
            </Text>
          ) : null}
          {pageError ? (
            <Stack
              direction="horizontal"
              className="items-center gap-2 px-5 py-2"
            >
              <Text
                testID="watchlist-position-popout-page-error"
                variant="caption"
                className="text-status-error"
              >
                {pageError}
              </Text>
              <Button
                testID="watchlist-position-popout-retry"
                label="Retry"
                variant="outline"
                tone="neutral"
                size="xs"
                onPress={onRetryPage}
              />
            </Stack>
          ) : null}
          {!isLoading && rows.length === 0 && squadRows.length === 0 ? (
            <Text
              testID="watchlist-position-popout-empty"
              variant="caption"
              className="px-5 py-2 text-content-muted"
            >
              {search ? noMatchCopy(search) : "No players here."}
            </Text>
          ) : null}
          <WatchlistOrderDndContext
            playerIds={rows.map(row => row.playerId)}
            playerNames={playerNames}
            disabled={!canEdit || Boolean(search)}
            onReorder={onReorder}
          >
            {/* W75: eight 44 px rows (352) show before the list scrolls —
                `max-h-90` (360) is the closed rung; rows sit on their own
                dividers, so the list carries no gap. */}
            <ScrollView
              testID="watchlist-position-popout-list"
              className="max-h-90"
            >
              <Stack testID="watchlist-position-popout-rows">
                {squadRows.map(row => (
                  <WatchlistPositionPopoutRow
                    key={row.playerId}
                    row={row}
                    canEdit={false}
                    paused
                    isFirst={false}
                    isLast={false}
                    onMove={onMove}
                  />
                ))}
                {rows.map((row, index) => (
                  <WatchlistPositionPopoutRow
                    key={row.playerId}
                    row={row}
                    canEdit={canEdit}
                    paused={Boolean(search)}
                    isFirst={index === 0}
                    isLast={index === rows.length - 1}
                    onMove={onMove}
                    rowAction={rowAction}
                  />
                ))}
              </Stack>
            </ScrollView>
          </WatchlistOrderDndContext>
        </Stack>
      </Box>
      {renderFooter({
        slot,
        copy: squadCopy(copy, squadRows.length),
        opening,
        totalCount,
        showOpenInTable,
        rowAction,
        onOpenInTable,
      })}
    </ModalContent>
  </Modal>
);

WatchlistPositionPopoutView.displayName = "WatchlistPositionPopoutView";
export default memo(WatchlistPositionPopoutView);
